import React, { useState } from 'react';
import type { CmsPage } from '../../types';
import { X, Settings } from 'lucide-react';

interface PageSettingsModalProps {
  isOpen: boolean;
  page: CmsPage;
  allPages: CmsPage[];
  onClose: () => void;
  onSave: (updated: Partial<CmsPage>) => void;
}

export const PageSettingsModal: React.FC<PageSettingsModalProps> = ({
  isOpen,
  page,
  allPages,
  onClose,
  onSave,
}) => {
  const [pageName, setPageName] = useState(page.pageName || '');
  const [slug, setSlug] = useState(page.slug || '');
  const [metaDescription, setMetaDescription] = useState(page.metaDescription || '');
  const [navParentPageId, setNavParentPageId] = useState<number | null>(page.navParentPageId ?? null);
  const [isMainMenuItem, setIsMainMenuItem] = useState(Boolean(page.isMainMenuItem));

  if (!isOpen) return null;

  const handleApply = (e: React.FormEvent) => {
    e.preventDefault();
    onSave({
      pageName,
      slug: slug.trim().toLowerCase().replace(/[^a-z0-9\-_/]/g, '-'),
      metaDescription,
      navParentPageId,
      isMainMenuItem,
    });
    onClose();
  };

  return (
    <div className="modal-backdrop-custom" onClick={onClose}>
      <div className="modal-dialog modal-dialog-centered" onClick={(e) => e.stopPropagation()}>
        <div className="modal-content shadow-lg">
          <form onSubmit={handleApply}>
            <div className="modal-header">
              <h5 className="modal-title d-flex align-items-center gap-2">
                <Settings size={18} /> Page Settings
              </h5>
              <button type="button" className="btn-close" onClick={onClose}></button>
            </div>

            <div className="modal-body d-flex flex-column gap-3">
              <div>
                <label className="form-label fw-semibold">Page Name / Title</label>
                <input
                  type="text"
                  className="form-control"
                  required
                  value={pageName}
                  onChange={(e) => setPageName(e.target.value)}
                />
              </div>

              <div>
                <label className="form-label fw-semibold">URL Slug</label>
                <div className="input-group">
                  <span className="input-group-text">/</span>
                  <input
                    type="text"
                    className="form-control"
                    required
                    value={slug}
                    onChange={(e) => setSlug(e.target.value)}
                  />
                </div>
                <div className="form-text small">Use &quot;index&quot; for the home page.</div>
              </div>

              <div>
                <label className="form-label fw-semibold">Meta Description (SEO)</label>
                <textarea
                  className="form-control"
                  rows={2}
                  value={metaDescription}
                  onChange={(e) => setMetaDescription(e.target.value)}
                  placeholder="Summary for search engines..."
                />
              </div>

              <div>
                <label className="form-label fw-semibold">Parent Navigation Page</label>
                <select
                  className="form-select"
                  value={navParentPageId || ''}
                  onChange={(e) => setNavParentPageId(e.target.value ? parseInt(e.target.value, 10) : null)}
                >
                  <option value="">None (Top Level)</option>
                  {allPages
                    .filter((p) => p.id && p.id !== page.id)
                    .map((p) => (
                      <option key={p.id} value={p.id!}>
                        {p.pageName} (/{p.slug})
                      </option>
                    ))}
                </select>
              </div>

              <div className="form-check">
                <input
                  className="form-check-input"
                  type="checkbox"
                  id="mainMenuCheck"
                  checked={isMainMenuItem}
                  onChange={(e) => setIsMainMenuItem(e.target.checked)}
                />
                <label className="form-check-label fw-semibold" htmlFor="mainMenuCheck">
                  Show in Main Navigation Menu
                </label>
              </div>
            </div>

            <div className="modal-footer">
              <button type="button" className="btn btn-outline-secondary" onClick={onClose}>
                Cancel
              </button>
              <button type="submit" className="btn btn-primary">
                Apply Settings
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};
